/**
 * "Your wall color" picker on wall art collections (snippets/c365-wall-picker.liquid, brief §9).
 *
 * Picking a wall:
 *  - tints every wall art card (CSS variable --c365-wall on <html>),
 *  - remembers the choice across pages and visits (localStorage "c365.wall"),
 *  - filters the grid to the art colors that pair with that wall, through the collection's Color filter.
 * Picking the same wall again clears it. ?wall=<name> in the URL also selects a wall (ads can link to it).
 */
(() => {
  const KEY = 'c365.wall';
  const read = () => { try { return localStorage.getItem(KEY); } catch (e) { return null; } };
  const write = (v) => { try { v ? localStorage.setItem(KEY, v) : localStorage.removeItem(KEY); } catch (e) { /* private mode */ } };
  // Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
  const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
  const T = (k, f) => D(window.C365_T && window.C365_T[k]) || f;
  const label = (name) => D(window.C365_T && window.C365_T['wall.names'] && window.C365_T['wall.names'][name]) || name;
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  class WallPicker extends HTMLElement {
    connectedCallback() {
      this.walls = JSON.parse(this.querySelector('[data-walls]')?.textContent || '[]');
      this.filter = JSON.parse(this.querySelector('[data-filter]')?.textContent || 'null');
      this.btn = this.querySelector('[data-wall-open]');
      this.pop = this.querySelector('[data-wall-pop]');
      this.list = this.querySelector('[data-wall-list]');
      this.note = this.querySelector('[data-wall-note]');
      this.label = this.querySelector('[data-wall-label]');
      this.dot = this.querySelector('[data-wall-dot]');

      const fromUrl = new URLSearchParams(location.search).get('wall');
      const wanted = fromUrl ? this.walls.find((w) => slug(w.name) === slug(fromUrl)) : null;
      this.current = wanted || this.walls.find((w) => w.name === read()) || null;
      if (wanted) write(wanted.name);

      this.render();
      // first paint: no color transition (it can stall when the tint lands during page load)
      const html = document.documentElement;
      html.classList.add('c365-wall-init');
      this.apply();
      requestAnimationFrame(() => requestAnimationFrame(() => html.classList.remove('c365-wall-init')));
      this.btn.addEventListener('click', () => this.toggle());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.toggle(false); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.toggle(false); });
    }

    toggle(open = this.pop.hidden) {
      this.pop.hidden = !open;
      this.btn.setAttribute('aria-expanded', String(open));
      if (open) this.list.querySelector('[aria-pressed="true"], button')?.focus();
    }

    render() {
      this.list.innerHTML = '';
      for (const w of this.walls) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'c365-wall__opt';
        b.setAttribute('aria-pressed', String(this.current?.name === w.name));
        b.innerHTML = `<span class="c365-wall__sw" style="background:${w.hex}"></span><span></span>`;
        b.lastElementChild.textContent = label(w.name);
        b.addEventListener('click', () => this.pick(w));
        this.list.append(b);
      }
      if (this.current) {
        this.note.textContent = `${this.current.why} ${T('wall.note', 'Showing artworks that look great on __NAME__ walls.').replace('__NAME__', label(this.current.name).toLowerCase())}`;
        this.label.textContent = T('wall.label_picked', 'Wall: __NAME__').replace('__NAME__', label(this.current.name));
        this.dot.style.background = this.current.hex;
      } else {
        this.label.textContent = T('wall.label', 'Wall');
        this.dot.style.background = '';
      }
    }

    apply() {
      const html = document.documentElement;
      this.current ? html.style.setProperty('--c365-wall', this.current.hex) : html.style.removeProperty('--c365-wall');
      // cards swap the white mockup for the flat artwork on the wall color
      html.classList.toggle('c365-wall-on', !!this.current);
    }

    pick(w) {
      const same = this.current?.name === w.name;
      this.current = same ? null : w;
      write(this.current?.name || null);
      this.render();
      this.apply();
      this.toggle(false);
      if (!this.filter) return; // collection has no Color filter yet: tint only
      const url = new URL(this.filter.clear, location.origin);
      url.searchParams.delete('page');
      if (this.current) {
        for (const label of this.current.colors) {
          const value = this.filter.values[label];
          if (value) url.searchParams.append(this.filter.param, value);
        }
      }
      location.assign(url.pathname + url.search);
    }
  }

  customElements.get('c365-wall-picker') || customElements.define('c365-wall-picker', WallPicker);
})();
